import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Save, Star, Copy, Check, Trash2, Loader2, QrCode, ExternalLink, Upload } from "lucide-react";
import { api } from "../../lib/api";
import { withUtm } from "../../lib/utm";

const card = "rounded-2xl border border-white/10 bg-surface/60 p-5";
const inputCls = "mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold";
const lbl = (t) => <span className="text-[11px] uppercase tracking-widest text-white/40">{t}</span>;
const SOURCES = ["tiktok", "instagram", "youtube", "facebook", "whatsapp", "qr", "email", "ads", "influencer", "playlist"];

const EMPTY = {
  slug: "", title: "", headline: "New Release", description: "", releaseId: "", campaignType: "song", ctaType: "listen",
  ctaLabel: "Listen Now", artwork: "", videoUrl: "", spotifyURL: "", appleMusicURL: "", youtubeURL: "", youtubeMusicURL: "",
  releaseDate: "", socialImage: "", utmSource: "", utmMedium: "", utmCampaign: "", active: false, published: true,
};

function ImageField({ label, value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", f);
    try { const r = await api.post("/admin/upload", form); onChange(r.data.url); } catch {}
    setBusy(false);
    e.target.value = "";
  };
  return (
    <label className="block">{lbl(label)}
      <div className="mt-1 flex items-center gap-2">
        {value && <img src={value} alt="" className="h-10 w-10 rounded-md border border-white/10 object-cover" />}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload" className="w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-3 py-2 text-[11px] font-bold uppercase text-gold disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
      </div>
    </label>
  );
}

function CampaignForm({ initial, releases, onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickRelease = (id) => {
    const r = releases.find((x) => x.internalId === id);
    set("releaseId", id);
    if (r && !initial?.internalId) {
      setF((p) => ({ ...p, releaseId: id, title: p.title || r.title, slug: p.slug || r.slug.replace(/-/g, "").slice(0, 16), releaseDate: r.releaseDate || "", campaignType: r.releaseType === "single" ? "song" : "album" }));
    }
  };
  const save = async () => {
    setSaving(true); setErr("");
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
    body.slug = f.slug; body.title = f.title; body.headline = f.headline || "New Release"; body.ctaLabel = f.ctaLabel || "Listen Now";
    try {
      if (initial?.internalId) await api.patch(`/admin/campaign-pages/${initial.internalId}`, body);
      else await api.post("/admin/campaign-pages", body);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save campaign"); }
    setSaving(false);
  };
  return (
    <div className={`${card} border-gold/30`} data-testid="campaign-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <label>{lbl("Linked release (optional)")}
          <select data-testid="campaign-release" value={f.releaseId || ""} onChange={(e) => pickRelease(e.target.value)} className={inputCls}>
            <option value="">— none (video / promo) —</option>
            {releases.map((r) => <option key={r.internalId} value={r.internalId}>{r.title} ({r.releaseType})</option>)}
          </select>
        </label>
        <label>{lbl("Short URL  djcodigo.com/")}<input data-testid="campaign-slug" value={f.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="unamas" className={inputCls} /></label>
        <label>{lbl("Title")}<input data-testid="campaign-title-input" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></label>
        <label>{lbl("Headline / badge")}<input data-testid="campaign-headline" value={f.headline} onChange={(e) => set("headline", e.target.value)} placeholder="New Release · Out Now · Watch" className={inputCls} /></label>
        <label className="sm:col-span-2">{lbl("One strong line about the track")}<textarea data-testid="campaign-description" rows={2} value={f.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} /></label>
        <label>{lbl("Type")}
          <select value={f.campaignType} onChange={(e) => set("campaignType", e.target.value)} className={inputCls}>
            {["song", "album", "video", "promo"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label>{lbl("Primary CTA")}
          <select data-testid="campaign-cta-type" value={f.ctaType} onChange={(e) => { set("ctaType", e.target.value); set("ctaLabel", e.target.value === "watch" ? "Watch Video" : "Listen Now"); }} className={inputCls}>
            <option value="listen">Listen (in-page player)</option><option value="watch">Watch (YouTube video)</option>
          </select>
        </label>
        <label>{lbl("CTA label")}<input value={f.ctaLabel} onChange={(e) => set("ctaLabel", e.target.value)} className={inputCls} /></label>
        <label>{lbl("Release date")}<input type="date" value={(f.releaseDate || "").slice(0, 10)} onChange={(e) => set("releaseDate", e.target.value)} className={inputCls} /></label>
        <label>{lbl("YouTube video URL")}<input data-testid="campaign-video" value={f.videoUrl || ""} onChange={(e) => set("videoUrl", e.target.value)} placeholder="https://youtu.be/…" className={inputCls} /></label>
        <label>{lbl("Spotify URL (override)")}<input value={f.spotifyURL || ""} onChange={(e) => set("spotifyURL", e.target.value)} className={inputCls} /></label>
        <label>{lbl("Apple Music URL (override)")}<input value={f.appleMusicURL || ""} onChange={(e) => set("appleMusicURL", e.target.value)} className={inputCls} /></label>
        <label>{lbl("YouTube Music URL (override)")}<input value={f.youtubeMusicURL || ""} onChange={(e) => set("youtubeMusicURL", e.target.value)} className={inputCls} /></label>
        <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
          <ImageField label="Artwork (override)" value={f.artwork} onChange={(v) => set("artwork", v)} />
          <ImageField label="Social sharing image" value={f.socialImage} onChange={(v) => set("socialImage", v)} />
        </div>
        <label>{lbl("Default utm_source")}<input value={f.utmSource || ""} onChange={(e) => set("utmSource", e.target.value)} placeholder="campaign_page" className={inputCls} /></label>
        <label>{lbl("Default utm_campaign")}<input value={f.utmCampaign || ""} onChange={(e) => set("utmCampaign", e.target.value)} placeholder={f.slug || "slug"} className={inputCls} /></label>
        <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} /> Published (page is live)</label>
        <label className="flex items-center gap-2 text-sm text-white/70"><input data-testid="campaign-active" type="checkbox" checked={!!f.active} onChange={(e) => set("active", e.target.checked)} /> Make this the homepage campaign</label>
      </div>
      {err && <p data-testid="campaign-error" className="mt-3 text-sm text-red-300">{err}</p>}
      <div className="mt-4 flex gap-2">
        <button data-testid="campaign-save" onClick={save} disabled={saving || !f.slug || !f.title} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase text-black disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Campaign
        </button>
        <button onClick={onCancel} className="rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold uppercase text-white/60">Cancel</button>
      </div>
    </div>
  );
}

function LinkBuilder({ url, slug }) {
  const [source, setSource] = useState("tiktok");
  const [copied, setCopied] = useState(false);
  const full = withUtm(url, { source, medium: source === "qr" ? "offline" : source === "email" ? "email" : source === "ads" ? "paid" : "social", campaign: slug });
  const copy = async () => { try { await navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {} };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <select data-testid="link-source" value={source} onChange={(e) => setSource(e.target.value)} className="rounded-lg border border-white/15 bg-ink/60 px-2 py-1.5 text-xs text-white">
        {SOURCES.map((s) => <option key={s}>{s}</option>)}
      </select>
      <code className="min-w-0 flex-1 truncate rounded-lg bg-ink/60 px-3 py-1.5 text-[11px] text-white/70">{full}</code>
      <button data-testid="copy-utm-link" onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-bold uppercase text-white/70">
        {copied ? <Check size={12} className="text-gold" /> : <Copy size={12} />} {copied ? "Copied" : "Copy tracked link"}
      </button>
    </div>
  );
}

function QrModal({ url, slug, onClose }) {
  const ref = useRef(null);
  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `djcodigo-${slug}-qr.png`;
    a.click();
  };
  const qrUrl = withUtm(url, { source: "qr", medium: "offline", campaign: slug });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={onClose}>
      <div className={`${card} w-full max-w-sm text-center`} onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-gold">QR • djcodigo.com/{slug}</p>
        <div ref={ref} className="inline-block rounded-2xl bg-white p-4"><QRCodeCanvas value={qrUrl} size={512} level="H" includeMargin={false} style={{ width: 240, height: 240 }} /></div>
        <p className="mt-3 break-all text-[11px] text-white/40">{qrUrl}</p>
        <div className="mt-4 flex justify-center gap-2">
          <button data-testid="qr-download" onClick={download} className="rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase text-black">Download PNG</button>
          <button onClick={onClose} className="rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold uppercase text-white/60">Close</button>
        </div>
      </div>
    </div>
  );
}

export function CampaignPagesAdmin() {
  const [rows, setRows] = useState([]);
  const [releases, setReleases] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | campaign
  const [qr, setQr] = useState(null);
  const origin = window.location.origin;

  const load = () => Promise.all([api.get("/admin/campaign-pages"), api.get("/admin/releases")])
    .then(([c, r]) => { setRows(c.data); setReleases(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const activate = async (id) => { await api.post(`/admin/campaign-pages/${id}/activate`).catch(() => {}); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this campaign page?")) return; await api.delete(`/admin/campaign-pages/${id}`).catch(() => {}); load(); };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Campaign Pages</h2>
          <p className="mt-1 text-xs text-white/40">Short URLs for TikTok, Instagram, YouTube, ads and QR codes. The active campaign drives the homepage hero.</p>
        </div>
        <button data-testid="new-campaign" onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-xs font-bold uppercase text-black"><Plus size={14} /> New Campaign</button>
      </div>

      {editing && !editing.internalId && <div className="mb-6"><CampaignForm initial={{}} releases={releases} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /></div>}

      <div className="space-y-4">
        {rows.map((c) => (
          <div key={c.internalId} className={`${card} ${c.active ? "border-gold/40" : ""}`} data-testid={`campaign-row-${c.slug}`}>
            {editing?.internalId === c.internalId ? (
              <CampaignForm initial={c} releases={releases} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex flex-wrap items-start gap-4">
                  {(c.artwork) && <img src={c.artwork} alt="" className="h-16 w-16 rounded-lg border border-white/10 object-cover" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-white">{c.title}</p>
                      {c.active && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold uppercase text-black">Homepage</span>}
                      {!c.published && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase text-white/60">Draft</span>}
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{c.campaignType} • {c.ctaType}</span>
                    </div>
                    <a href={`${origin}/${c.slug}`} target="_blank" rel="noreferrer" data-testid={`campaign-url-${c.slug}`} className="mt-1 inline-flex items-center gap-1 text-sm text-gold hover:underline">{origin.replace(/^https?:\/\//, "")}/{c.slug} <ExternalLink size={12} /></a>
                    <div className="mt-2 flex flex-wrap gap-4 text-xs text-white/60">
                      <span>Views <b className="text-white">{c.stats?.views ?? 0}</b></span>
                      <span>CTA <b className="text-white">{c.stats?.ctaClicks ?? 0}</b></span>
                      <span>Spotify <b className="text-white">{c.stats?.spotifyClicks ?? 0}</b></span>
                      <span>Signups <b className="text-white">{c.stats?.signups ?? 0}</b></span>
                      <span>Shares <b className="text-white">{c.stats?.shares ?? 0}</b></span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!c.active && <button data-testid={`activate-${c.slug}`} onClick={() => activate(c.internalId)} className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-[11px] font-bold uppercase text-gold"><Star size={12} /> Set as homepage</button>}
                    <button data-testid={`qr-${c.slug}`} onClick={() => setQr(c)} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-bold uppercase text-white/70"><QrCode size={12} /> QR</button>
                    <button data-testid={`edit-${c.slug}`} onClick={() => setEditing(c)} className="rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-bold uppercase text-white/70">Edit</button>
                    <button data-testid={`delete-${c.slug}`} onClick={() => remove(c.internalId)} className="rounded-full border border-red-400/30 px-3 py-1.5 text-[11px] font-bold uppercase text-red-300"><Trash2 size={12} /></button>
                  </div>
                </div>
                <LinkBuilder url={`${origin}/${c.slug}`} slug={c.utmCampaign || c.slug} />
              </>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-white/40">No campaign pages yet. Create one for your current single.</p>}
      </div>
      {qr && <QrModal url={`${origin}/${qr.slug}`} slug={qr.slug} onClose={() => setQr(null)} />}
    </div>
  );
}
